// src/pages/BlogShowroom.js
/* eslint-disable */
import React, { useState, useEffect } from 'react';
import WebpageShell from './WebpageShell';
import HelmetTemplate from '../utils/HelmetTemplate';

// Firebase imports
import { collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase'; // your firebase config

const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="w-full h-64 bg-gray-300"></div>
    <div className="p-4 space-y-2">
      <div className="h-6 bg-gray-300 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 rounded w-full"></div>
      <div className="h-4 bg-gray-300 rounded w-5/6"></div>
    </div>
  </div>
);

const SkeletonCardMobile = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="w-full h-40 bg-gray-300"></div>
    <div className="p-2 space-y-2">
      <div className="h-5 bg-gray-300 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 rounded w-full"></div>
    </div>
  </div>
);

const INITIAL_DESKTOP = 6;   // how many cards to show first on >= md
const STEP_DESKTOP     = 6;  // how many more to reveal each click
const INITIAL_MOBILE   = 4;  // how many cards to show first on < md
const STEP_MOBILE      = 4;  // how many more to reveal each click

export default function BlogShowroom() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [visibleCount, setVisibleCount] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 768 ? INITIAL_DESKTOP : INITIAL_MOBILE
  );

  useEffect(() => {
    const fetchBlogPosts = async () => {
      setLoading(true);
      const posts = [];
      try {
        const querySnapshot = await getDocs(collection(db, 'blogPosts'));
        const storage = getStorage();
        for (const docSnap of querySnapshot.docs) {
          const data = docSnap.data();
          const title = data.title;
          const date = data.date;
          const author           = data.author || '';
          const shortDescription = data.shortDescription || '';
          const formattedTitle = title.replace(/[\s:]+/g, '_');
          const imagePath = `blog_posts/images/${formattedTitle}/${formattedTitle}-banner.webp`;

          let imageUrl = '';
          try {
            imageUrl = await getDownloadURL(ref(storage, imagePath));
          } catch (error) {
            console.error(`Error fetching image for ${title}:`, error);
            imageUrl = 'https://via.placeholder.com/300';
          }
          posts.push({
            id: docSnap.id,
            title,
            formattedTitle,
            date,
            author,
            shortDescription,
            image: imageUrl,
          });
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }

      posts.sort((a, b) => {
        const dateA = a.date && a.date.toDate ? a.date.toDate() : new Date(a.date);
        const dateB = b.date && b.date.toDate ? b.date.toDate() : new Date(b.date);
        return dateB - dateA;
      });
      setBlogPosts(posts);
    };

    fetchBlogPosts();
  }, []);

  const handleToggle = () => {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
    const step = isDesktop ? STEP_DESKTOP : STEP_MOBILE;

    setVisibleCount(prev =>
      prev >= blogPosts.length
        ? (isDesktop ? INITIAL_DESKTOP : INITIAL_MOBILE)
        : Math.min(prev + step, blogPosts.length)
    );
  };

  const showToggle = !loading && blogPosts.length > (
    (typeof window !== 'undefined' && window.innerWidth >= 768) ? INITIAL_DESKTOP : INITIAL_MOBILE
  );

  return (
    <WebpageShell
      navProps={{ addAnnouncementBar: false, isRecPrototypePage: false }}
      showCTA={true}
      showFooter={true}
    >
      <HelmetTemplate
        title="Blog - Match Medic"
        description="Discover the latest developments, insights, and opinion pieces in the hair transplant industry. Read articles, guides, and blogs from Match Medic."
        canonicalUrl="https://matchmedic.com/blog-showroom"
      />
      {/* Page content container (keeps your previous 3/4 width on md+) */}
      <div className="mx-auto w-full md:w-4/5 mt-8">
        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-3 gap-8">
            <aside
              className="sticky top-16 self-start col-span-1 p-4 border-r-2"
            >            
            <h2 className="text-4xl font-extrabold mb-4 text-gray-900">
              Articles, Guides & Blogs
            </h2>
            <p className="mb-6 mt-6 text-xl font-normal text-gray-600">
              Discover the latest developments, insights, and opinion pieces in the hair transplant industry.
            </p>
            <h3 className="text-xl font-extrabold mb-1 text-gray-900">
              Latest Articles
            </h3>
            <ul className="space-y-2">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <li key={i} className="h-4 bg-gray-200 animate-pulse rounded w-3/4 my-2" />
                ))
              ) : blogPosts.length === 0 ? (
                <li>No blog posts found.</li>
              ) : (
                blogPosts.slice(0, 5).map((blog) => (
                  <li
                    key={blog.id}
                    className="text-sm font-light cursor-pointer hover:underline"
                    onClick={() =>
                      window.location.href = `/blog/${blog.formattedTitle}`
                    }
                  >
                    {blog.title}
                  </li>
                ))
              )}
            </ul>
          </aside>

          <main className="col-span-2">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {blogPosts.slice(0, visibleCount).map(blog => (
                    <article
                      key={blog.id}
                      className="bg-white group rounded-lg shadow-md overflow-hidden cursor-pointer hover:bg-gray-100"
                      onClick={() => (window.location.href = `/blog/${blog.formattedTitle}`)}
                    >
                      <img
                        loading="lazy"
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-4">
                        <h3
                          className="text-2xl font-extrabold mb-2 text-gray-800 group-hover:underline
                                     line-clamp-3 min-h-[96px]"
                        >
                          {blog.title}
                        </h3>

                        <div className="h-px bg-gray-200 mb-2" />

                        <p className="text-sm font-medium text-gray-500 mb-3">
                          {blog.date}
                          {blog.author && ` · ${blog.author}`}
                        </p>

                        <p className="text-gray-700">{blog.shortDescription}</p>
                      </div>
                    </article>
                  ))}
                </div>

                {showToggle && (
                  <div className="col-span-full flex justify-center mt-6">
                    <button
                      onClick={handleToggle}
                      className="px-6 py-2 bg-gray-900  text-white rounded-md"
                    >
                      {visibleCount >= blogPosts.length ? 'See less' : 'See more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <header>
            <h2 className="text-4xl font-extrabold mb-4 text-gray-900 text-left">
              Articles, Guides & Blogs
            </h2>
            <p className="mb-3 mt-3 text-xl font-normal text-gray-600">
              Discover the latest developments, insights, and opinion pieces in the hair transplant industry.
            </p>
          </header>

          <div className="mb-4">
            <details className="bg-gray-100 rounded-lg p-2">
              <summary className="text-xl font-extrabold text-gray-900 cursor-pointer">
                Latest Articles
              </summary>
              <ul className="mt-2 space-y-2">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <li key={i} className="h-4 bg-gray-200 animate-pulse rounded w-3/4 my-2" />
                  ))
                ) : blogPosts.length === 0 ? (
                  <li>No blog posts found.</li>
                ) : (
                  blogPosts.slice(0, 5).map((blog) => (
                    <li
                      key={blog.id}
                      className="text-sm font-light cursor-pointer hover:underline"
                      onClick={() =>
                        window.location.href = `/blog/${blog.formattedTitle}`
                      }
                    >
                      {blog.title}
                    </li>
                  ))
                )}
              </ul>
            </details>
          </div>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => <SkeletonCardMobile key={i} />)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  {blogPosts.slice(0, visibleCount).map(blog => (
                    <article
                      key={blog.id}
                      className="bg-white group rounded-lg shadow-md overflow-hidden cursor-pointer hover:bg-gray-100"
                      onClick={() => (window.location.href = `/blog/${blog.formattedTitle}`)}
                    >
                      <img
                        loading="lazy"
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-2">
                        <h3
                          className="text-lg font-extrabold mb-1 text-gray-800 group-hover:underline
                                     line-clamp-3 min-h-[96px]"
                        >
                          {blog.title}
                        </h3>

                        <div className="h-px bg-gray-200 my-1" />

                        <p className="text-[11px] font-medium text-gray-500 mb-2">
                          {blog.date}
                          {blog.author && ` · ${blog.author}`}
                        </p>

                        <p className="text-gray-700 text-sm">{blog.shortDescription}</p>
                      </div>
                    </article>
                  ))}
                </div>

                {showToggle && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={handleToggle}
                      className="px-5 py-2 bg-gray-900 hover:bg-mm_blue-dark text-white rounded-md transition w-full"
                    >
                      {visibleCount >= blogPosts.length ? 'See less' : 'See more →'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </WebpageShell>
  );
}
